/** Key figures of the stand and, if configured, a short generated description of it. */
import { SimpleGrid, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { StatCard } from '@/components/StatCard';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import { Section } from './Section';
import { useSpeciesName } from './species';
import type { TreeStats } from './stats';

export interface SummarySectionProps {
  stats: TreeStats;
  prose: boolean;
  showMaturity: boolean;
  showStrata: boolean;
}

export function SummarySection({ stats, prose, showMaturity, showStrata }: SummarySectionProps) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const name = useSpeciesName();
  const s = stats.summary;
  const dash = '—';
  const fmt = (v: number | null, digits = 0) => (v === null ? dash : number(v, digits));

  const sentences: string[] = [];
  if (prose && s.count) {
    const area = number(stats.area.ha, 1);
    const intro = Number(stats.area.ha.toFixed(1)) > 0 ? 'trees.prose.intro' : 'trees.prose.introNoArea';
    sentences.push(t(intro, { count: s.count, countText: number(s.count), area }));
    const top = stats.species.rows.filter((r) => r.isSpecies).slice(0, 5);
    if (top.length) sentences.push(t('trees.prose.species', { list: top.map((r) => `${name(r.genus)} (${number(r.countPct, 0)} %)`).join(', ') }));
    if (s.meanDbh !== null && s.meanHeight !== null) sentences.push(t('trees.prose.dimensions', { dbh: number(s.meanDbh, 1), height: number(s.meanHeight, 1) }));
    sentences.push(s.volumePerHa === null ? t('trees.prose.volume', { volume: number(s.volume, 0) }) : t('trees.prose.volumePerHa', { volume: number(s.volume, 0), perHa: number(s.volumePerHa, 0) }));
    if (showMaturity) sentences.push(t('trees.prose.maturity', { count: stats.maturity.matureCount, countText: number(stats.maturity.matureCount), pct: number(stats.maturity.maturePct, 0) }));
    if (showStrata && stats.strata) {
      const c = stats.strata.counts;
      const withHeight = c.upper + c.middle + c.lower;
      const pct = (n: number) => number((n / withHeight) * 100, 0);
      sentences.push(t('trees.prose.strata', { upper: pct(c.upper), middle: pct(c.middle), lower: pct(c.lower) }));
    }
  }

  return (
    <Section title={t('trees.sections.summary')} className="report-summary">
      <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="md">
        <StatCard label={t('trees.kpi.count')} value={number(s.count)} accent testId="stat-count" />
        <StatCard label={t('trees.kpi.volume')} value={number(s.volume, 0)} unit="m³" testId="stat-volume" />
        <StatCard label={t('trees.kpi.volumePerHa')} value={fmt(s.volumePerHa, 0)} unit={s.volumePerHa === null ? undefined : 'm³/ha'} testId="stat-volume-per-ha" />
        <StatCard label={t('trees.kpi.area')} value={number(stats.area.ha, 2)} unit="ha" sub={t(`trees.kpi.areaSource.${stats.area.source}`)} testId="stat-area" />
        <StatCard label={t('trees.kpi.meanDbh')} value={fmt(s.meanDbh, 1)} unit={s.meanDbh === null ? undefined : 'cm'} testId="stat-mean-dbh" />
        <StatCard label={t('trees.kpi.meanHeight')} value={fmt(s.meanHeight, 1)} unit={s.meanHeight === null ? undefined : 'm'} testId="stat-mean-height" />
      </SimpleGrid>
      {sentences.length > 0 && (
        <Text mt="md" fz={15} c={brand.text} maw={820}>
          {sentences.join(' ')}
        </Text>
      )}
    </Section>
  );
}
